import { useMemo, useState } from "react";
import { Copy, KeyRound, Plus, Webhook } from "lucide-react";
import { useTranslations } from "use-intl";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { showError } from "@/lib/toast";
import { useWorkflowWebhooks } from "@/hooks/api/queries/workflows.queries";
import { useCreateWorkflowWebhook } from "@/hooks/api/mutations/workflows.mutations";
import type { Workflow } from "@/types/workflow.types";

interface WebhookTriggerSurfaceProps {
  workflow: Workflow;
}

function publicWebhookURL(publicId: string) {
  const path = `/v1/workflow-webhooks/${publicId}`;
  const apiBase = import.meta.env.VITE_HUB_API_URL as string | undefined;
  if (apiBase?.startsWith("http")) {
    return `${apiBase.replace(/\/+$/, "")}${path}`;
  }
  return path;
}

export function WebhookTriggerSurface({ workflow }: WebhookTriggerSurfaceProps) {
  const t = useTranslations("workflows");
  const { data, isLoading } = useWorkflowWebhooks(workflow.id);
  const createWebhook = useCreateWorkflowWebhook();
  const [secret, setSecret] = useState<string | null>(null);
  const triggers = data?.data ?? [];
  const primaryTrigger = useMemo(
    () => triggers.find((trigger) => trigger.enabled) ?? triggers[0] ?? null,
    [triggers],
  );
  const canWrite =
    !workflow.effectiveActions ||
    workflow.effectiveActions.includes("write") ||
    workflow.effectiveActions.includes("admin");
  const hasRunAs = workflow.runAsPrincipalKind === "user" && !!workflow.runAsPrincipalId;
  const endpoint = primaryTrigger ? publicWebhookURL(primaryTrigger.publicId) : "";

  const copy = async (value: string, message: string) => {
    await navigator.clipboard.writeText(value);
    toast.success(message);
  };

  const create = () => {
    if (!canWrite || !hasRunAs) return;
    createWebhook.mutate(
      {
        workflowId: workflow.id,
        data: { name: t("trigger_webhook.default_name"), enabled: true },
      },
      {
        onSuccess: (res) => {
          setSecret(res.data.secret ?? null);
          toast.success(t("webhooks.toast_created"));
        },
        onError: (err) =>
          showError((err as { message?: string }).message || t("webhooks.toast_failed")),
      },
    );
  };

  return (
    <section className="rounded-lg border border-[var(--border)] bg-[var(--card)]">
      <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300">
            <Webhook className="size-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-[var(--foreground)]">
                {t("trigger_webhook.title")}
              </h3>
              {primaryTrigger && (
                <span
                  className={
                    primaryTrigger.enabled
                      ? "rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700"
                      : "rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                  }
                >
                  {primaryTrigger.enabled ? t("webhooks.enabled") : t("webhooks.disabled")}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              {primaryTrigger
                ? t("trigger_webhook.configured", { count: triggers.length })
                : t("trigger_webhook.empty")}
            </p>
            {primaryTrigger && (
              <button
                className="mt-2 block max-w-full truncate font-mono text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                onClick={() => copy(endpoint, t("webhooks.toast_path_copied"))}
              >
                {endpoint}
              </button>
            )}
            {!hasRunAs && (
              <p className="mt-2 text-xs text-amber-600">{t("webhooks.run_as_required")}</p>
            )}
            {!canWrite && (
              <p className="mt-2 text-xs text-[var(--muted-foreground)]">
                {t("webhooks.write_required")}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {primaryTrigger ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              startIcon={<Copy className="size-4" />}
              onClick={() => copy(endpoint, t("webhooks.toast_path_copied"))}
            >
              {t("trigger_webhook.copy_endpoint")}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="sm"
              startIcon={<Plus className="size-4" />}
              isLoading={createWebhook.isPending || isLoading}
              disabled={!canWrite || !hasRunAs}
              onClick={create}
            >
              {t("trigger_webhook.create")}
            </Button>
          )}
        </div>
      </div>
      {secret && (
        <div className="border-t border-[var(--border)] px-4 py-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <p className="min-w-0 break-all font-mono text-xs text-[var(--foreground)]">
              <KeyRound className="mr-1 inline size-3.5 align-[-2px]" />
              {secret}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              startIcon={<Copy className="size-4" />}
              onClick={() => copy(secret, t("webhooks.toast_copied"))}
            >
              {t("webhooks.copy_secret")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
